import React from 'react';
import { Button, Empty } from '@myua/kit';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from '../../app/actions';
import * as finderActions from '../../finder/actions';
import * as lastStateActions from '../../lastState/actions';
import * as actions from '../actions';
import { getCatalogFiltered } from '../emptyCatalog';
import {
  getCanUpload,
  getModalOpen,
  getUploadStats,
  getUploadToProjectEnabled,
} from '../selectors';
import { useUploadTarget } from '../useUploadTarget';
import { UploadDestination, UploadDropzone } from './UploadTargetFields';

/**
 * Empty catalog. In a project the user can upload to (typically a fresh "Your space")
 * the upload modal's destination picker and dropzone are shown right on the page, and
 * the progress then goes to the upload modal. With search/filters applied an empty
 * result means "nothing matched", so filters can be cleared instead.
 */
export const EmptyCatalogUpload: React.FC = () => {
  const dispatch = useDispatch();
  const enabled = useSelector(getUploadToProjectEnabled);
  const canUpload = useSelector(getCanUpload);
  const filtered = useSelector(getCatalogFiltered);
  const modalOpen = useSelector(getModalOpen);
  const { isUploading, total, successfulCount, failedCount } = useSelector(getUploadStats);
  const target = useUploadTarget();

  if (filtered) {
    const clearFilters = () => {
      dispatch(appActions.resetFilters({ preserveSearch: false }));
      dispatch(finderActions.loadAssetsRequested());
      dispatch(lastStateActions.saveRequested());
    };
    return (
      <Empty
        description={
          <>
            <div className="al-empty-catalog-upload__empty-title">No files match your filters</div>
            <div>Try a different search term or remove some filters.</div>
          </>
        }
      >
        <Button onClick={clearFilters}>Clear filters</Button>
      </Empty>
    );
  }

  if (!enabled) {
    return (
      <Empty
        description={
          canUpload
            ? 'No files found'
            : "This project is empty. You don't have permission to upload here."
        }
      />
    );
  }

  // The modal was closed during the upload: the files are not in the grid yet.
  if (isUploading && !modalOpen) {
    const finished = successfulCount + failedCount;
    return (
      <div className="al-upload-modal al-empty-catalog-upload">
        <div className="al-empty-catalog-upload__uploading">
          <div className="al-empty-catalog-upload__uploading-title">
            Uploading {finished} of {total} files…
          </div>
          <div className="al-upload-modal__progress-bar">
            <div
              className="al-upload-modal__progress-fill"
              style={{ width: `${total > 0 ? Math.round((finished / total) * 100) : 0}%` }}
            />
          </div>
          <Button type="link" onClick={() => dispatch(actions.modalOpened())}>
            View details
          </Button>
        </div>
      </div>
    );
  }

  const title = target.sidebarFolderName
    ? `“${target.sidebarFolderName}” is empty`
    : `${target.projectName ?? 'This project'} is empty`;
  const subtitle = target.sidebarFolderName
    ? 'Upload files to add them to this folder.'
    : 'Upload your first files to start building your asset library.';

  return (
    <div className="al-upload-modal al-empty-catalog-upload">
      <div className="al-empty-catalog-upload__title">{title}</div>
      <div className="al-empty-catalog-upload__subtitle">{subtitle}</div>
      <UploadDestination target={target} />
      <UploadDropzone
        target={target}
        onFiles={(files) => target.startUpload(files, { openModal: true })}
      />
    </div>
  );
};
